import type { IconName } from "../../components/ui/Icon";
import type { ModuleDefinition } from "../../runtime/contracts";
import { Icon } from "../../components/ui";
import { SettingsModule as SettingsModuleComponent } from "./SettingsModule";
import { createSettingsStore } from "./store";

export interface SettingsCategory {
  readonly id: string;
  readonly title: string;
  readonly icon: IconName;
  readonly enabled: boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export const CATEGORY_GROUPS: readonly (readonly SettingsCategory[])[] = [
  [
    { id: "accounts", title: "Accounts", icon: "plug", enabled: true },
    { id: "sources", title: "Sources", icon: "lock", enabled: true },
    { id: "appearance", title: "Appearance", icon: "palette", enabled: true },
    { id: "identity", title: "Identity", icon: "id-card", enabled: true },
    { id: "groups", title: "Groups", icon: "users", enabled: true },
    { id: "agent", title: "Agent", icon: "brain", enabled: true },
    { id: "providers", title: "Providers", icon: "plug", enabled: true },
    { id: "models", title: "Models", icon: "zap", enabled: true },
    { id: "billing", title: "Billing", icon: "wallet", enabled: true },
    { id: "search", title: "Search & Indexing", icon: "search", enabled: true },
    { id: "modules", title: "Modules", icon: "settings", enabled: true },
  ],
];

export const CATEGORIES: readonly SettingsCategory[] = CATEGORY_GROUPS.flat();

// eslint-disable-next-line react-refresh/only-export-components
export const CAPABILITY_LABELS: Readonly<Record<string, string>> = {
  chat: "Chat",
  reasoning: "Reasoning",
  embedding: "Embedding",
};

export const CAPABILITY_ORDER = [
  "chat",
  "reasoning",
  "embedding",
] as const;

export const ON_WARNING_OPTIONS = [
  {
    value: "block",
    label: "Block and show warning",
    description:
      "Prevents the action from completing and notifies the user.",
  },
  {
    value: "warn",
    label: "Warn but allow to proceed",
    description:
      "Shows a warning but lets the action go if manually approved.",
  },
  {
    value: "log",
    label: "Log only",
    description: "Records the warning silently without notifying.",
  },
] as const;

// eslint-disable-next-line react-refresh/only-export-components
export const AGENT_ICONS: Readonly<Record<string, IconName>> = {
  Psychologist: "brain",
  "Legal Advisor": "scale",
  "Code Reviewer": "code",
  Translator: "globe",
};

// eslint-disable-next-line react-refresh/only-export-components
export const ACCESS_LEVELS: readonly {
  value: string;
  label: string;
  showCount?: boolean;
}[] = [
  { value: "always", label: "Always Allow" },
  { value: "groups", label: "Allow for selected groups", showCount: true },
  { value: "ask", label: "Ask Every Time" },
];

// eslint-disable-next-line react-refresh/only-export-components
export const ACCESS_LABELS: Readonly<Record<string, string>> = {
  always: "Always Allow",
  groups: "Groups",
  ask: "Ask Every Time",
  never: "Never",
};

// eslint-disable-next-line react-refresh/only-export-components
export const GROUP_LABELS: Readonly<
  Record<string, { label: string; icon: "send" | "mail" | "zap" }>
> = {
  telegram: { label: "TELEGRAM", icon: "send" },
  email: { label: "EMAIL", icon: "mail" },
  gmail: { label: "EMAIL", icon: "mail" },
  general: { label: "GENERAL", icon: "zap" },
};

// eslint-disable-next-line react-refresh/only-export-components
export const SettingsModule: ModuleDefinition = {
  id: "settings",
  title: "Settings",
  icon: <Icon name="settings" size={26} />,
  Component: SettingsModuleComponent,
  createStore: (runtime) => {
    const store = createSettingsStore(runtime);
    return store;
  },
};
